/*--------------------------------------------------------------
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * See License.txt in the project root for license information.
 *--------------------------------------------------------------*/

import * as React from 'react';
import { getProductIdFromLink } from './actions/get-product-content-visibility.action';
import { IMoeV2ListArrowRightViewProps } from './moe-v2-list-arrow-right';

/**
 * Render the thumbnail item images.
 * @param props - The image list view props.
 * @returns Return HTML having IMoeV2ListArrowRightViewProps props with image List.
 */

export default (props: IMoeV2ListArrowRightViewProps) => {
    const { config } = props;

    if (!config?.items || config.items.length === 0) {
        return null;
    }

    if (!props.productVisibilityReady) {
        return null;
    }

    const hiddenProductIds = new Set(props.hiddenProductIds);

    const visibleItems = config.items.filter(item => {
        const productId = getProductIdFromLink(item.link);
        return productId === undefined || !hiddenProductIds.has(productId);
    });

    if (visibleItems.length === 0) {
        return null;
    }

    const renderArrowIcon = (): JSX.Element => (
        <svg
            className='moe-v2-list-arrow-right__arrow-icon'
            width='24'
            height='24'
            viewBox='0 0 24 24'
            fill='none'
            xmlns='http://www.w3.org/2000/svg'
            aria-hidden='true'
        >
            <path d='M5 12h14m-7-7 7 7-7 7' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round' />
        </svg>
    );

    const renderItem = (item: any, index: number): JSX.Element => {
        const hasLink = item.link?.destinationUrl;
        const ItemTag = hasLink ? 'a' : 'div';

        const itemProps = hasLink
            ? {
                  href: item.link.destinationUrl,
                  'aria-label': item.title
              }
            : {};

        return (
            <ItemTag
                key={index}
                className={`moe-v2-list-arrow-right__item ${hasLink ? 'moe-v2-list-arrow-right__item--clickable' : ''}`}
                {...itemProps}
            >
                <div className='moe-v2-list-arrow-right__content'>
                    {item.title && (
                        <h3 className='moe-v2-list-arrow-right__title' title={item.title}>
                            {item.title}
                        </h3>
                    )}
                    {item.description && (
                        <p className='moe-v2-list-arrow-right__description' title={item.description}>
                            {item.description}
                        </p>
                    )}
                </div>
                <div className='moe-v2-list-arrow-right__arrow'>{renderArrowIcon()}</div>
            </ItemTag>
        );
    };

    return (
        <div className={`moe-v2-module-container moe-v2-list-arrow-right`}>
            <div className='moe-v2-list-arrow-right__container'>
                <div className='moe-v2-list-arrow-right__grid'>{visibleItems.map((item, index) => renderItem(item, index))}</div>
            </div>
        </div>
    );
};
